<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import ButtonGroup from 'dashboard/components-next/buttonGroup/ButtonGroup.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useMapGetter } from 'dashboard/composables/store';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { computed } from 'vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const { updateUISettings, uiSettings } = useUISettings();

const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const getSelectedChat = useMapGetter('getSelectedChat');

// 当前对话
const currentChat = computed(() => getSelectedChat.value);

// 获取当前对话的账户
const getCurrentAccount = useMapGetter('accounts/getAccount');
const currentAccount = computed(() => {
  if (!currentChat.value?.account_id) return null;
  return getCurrentAccount.value(currentChat.value.account_id);
});

// 是否有营销云租户
const hasMarketingCloudTenantId = computed(() => {
  return currentAccount.value?.custom_attributes?.marketing_cloud_tenant_id;
});

const showCopilotTab = computed(() => {
  const isCaptainEnabled = isFeatureEnabledonAccount.value(
    currentAccountId.value,
    FEATURE_FLAGS.CAPTAIN
  );
  const isPilotEnabled = isFeatureEnabledonAccount.value(
    currentAccountId.value,
    FEATURE_FLAGS.PILOT
  );
  return isCaptainEnabled || isPilotEnabled;
});

const isContactSidebarOpen = computed(
  () => uiSettings.value.is_contact_sidebar_open
);
const isCopilotPanelOpen = computed(
  () => uiSettings.value.is_copilot_panel_open
);

// 当前侧边栏类型：'contact' 或 'marketing_cloud'
const sidebarType = computed(() => {
  return uiSettings.value.sidebar_type || 'contact';
});

const isPilotEnabled = computed(() =>
  isFeatureEnabledonAccount.value(currentAccountId.value, FEATURE_FLAGS.PILOT)
);

const toggleConversationSidebarToggle = () => {
  updateUISettings({
    is_contact_sidebar_open: !isContactSidebarOpen.value,
    is_copilot_panel_open: false,
  });
};

const handleConversationSidebarToggle = () => {
  updateUISettings({
    is_contact_sidebar_open: true,
    is_copilot_panel_open: false,
    sidebar_type: 'contact',
  });
};

const handleMarketingCloudSidebarToggle = () => {
  updateUISettings({
    is_contact_sidebar_open: true,
    is_copilot_panel_open: false,
    sidebar_type: 'marketing_cloud',
  });
};

const handleCopilotSidebarToggle = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: true,
  });
};

const keyboardEvents = {
  'Alt+KeyO': {
    action: toggleConversationSidebarToggle,
  },
};
useKeyboardEvents(keyboardEvents);
</script>

<template>
  <div
    class="gradient-border-wrapper absolute top-36 xl:top-24 ltr:right-2 rtl:left-2"
  >
    <ButtonGroup
      class="flex flex-col justify-center items-center bg-n-solid-2/95 backdrop-blur-lg rounded-full gap-1.5 p-1.5 shadow-sm transition-shadow duration-200 hover:shadow"
    >
      <Button
        v-tooltip.top="$t('CONVERSATION.SIDEBAR.CONTACT')"
        ghost
        slate
        sm
        class="!rounded-full transition-all duration-[250ms] ease-out active:!scale-95 active:!brightness-105 active:duration-75"
        :class="{
          'bg-n-alpha-2 active:shadow-sm':
            isContactSidebarOpen && sidebarType === 'contact',
        }"
        icon="i-ph-user-bold"
        @click="handleConversationSidebarToggle"
      />
      <Button
        v-if="hasMarketingCloudTenantId"
        v-tooltip.top="$t('CONVERSATION.SIDEBAR.MARKETING_CLOUD')"
        ghost
        slate
        sm
        class="!rounded-full transition-all duration-[250ms] ease-out active:!scale-95 active:!brightness-105 active:duration-75"
        :class="{
          'bg-n-alpha-2 active:shadow-sm':
            isContactSidebarOpen && sidebarType === 'marketing_cloud',
        }"
        icon="i-lucide-cloud"
        @click="handleMarketingCloudSidebarToggle"
      />
      <Button
        v-if="showCopilotTab"
        v-tooltip.bottom="$t('CONVERSATION.SIDEBAR.COPILOT')"
        ghost
        slate
        sm
        class="!rounded-full transition-all duration-[250ms] ease-out active:!scale-95 active:!brightness-105 active:duration-75"
        :class="{
          'bg-n-alpha-2 active:!brightness-105 active:shadow-sm':
            isCopilotPanelOpen,
        }"
        icon="i-ph-rocket-launch-fill"
        @click="handleCopilotSidebarToggle"
      />
    </ButtonGroup>
  </div>
</template>

<style scoped>
/* 渐变边框包裹器 */
.gradient-border-wrapper {
  z-index: 10;
  padding: 1.5px;
  border-radius: 9999px;
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #ec4899 100%);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* 彩色渐变图标样式 - 火箭图标 */
:deep(.i-ph-rocket-launch-fill) {
  background: linear-gradient(
    135deg,
    #3b82f6 0%,
    #8b5cf6 50%,
    #ec4899 100%
  ) !important;
  -webkit-mask-size: cover !important;
  mask-size: cover !important;
}
</style>
